/**
 * Field Correction Modal Component
 * Allows users to correct a single field with audit trail
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

interface CorrectFieldModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dailyLogId: number;
  fieldName: string;
  fieldLabel: string;
  currentValue: string | null;
  onSuccess: () => void;
}

export function CorrectFieldModal({
  open,
  onOpenChange,
  dailyLogId,
  fieldName,
  fieldLabel,
  currentValue,
  onSuccess,
}: CorrectFieldModalProps) {
  const [newValue, setNewValue] = useState(currentValue || "");
  const [correctionReason, setCorrectionReason] = useState("");

  const correctFieldMutation = trpc.dailyLogs.correctField.useMutation();

  const handleSubmit = async () => {
    if (!correctionReason.trim()) {
      toast.error("Correction reason is required");
      return;
    }

    if (newValue === currentValue) {
      toast.error("New value must be different from current value");
      return;
    }

    try {
      await correctFieldMutation.mutateAsync({
        dailyLogId,
        fieldName,
        newValue,
        correctionReason,
      });

      toast.success("Field corrected successfully");
      setNewValue("");
      setCorrectionReason("");
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || "Failed to correct field");
    }
  };

  const handleCancel = () => {
    setNewValue(currentValue || "");
    setCorrectionReason("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Correct {fieldLabel}</DialogTitle>
          <DialogDescription>
            Update this field value with a required correction reason for audit trail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="current-value">Current Value</Label>
            <Input
              id="current-value"
              value={currentValue || "(empty)"}
              disabled
              className="bg-muted"
            />
          </div>

          <div>
            <Label htmlFor="new-value">New Value *</Label>
            <Input
              id="new-value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="Enter corrected value"
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="reason">Correction Reason *</Label>
            <Textarea
              id="reason"
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              placeholder="Explain why this correction is needed (e.g., transcription error, meter misread, data entry mistake)"
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              This will be recorded in the audit trail
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={correctFieldMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={correctFieldMutation.isPending || !correctionReason.trim()}
          >
            {correctFieldMutation.isPending ? "Correcting..." : "Correct Field"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
